import React, { useEffect, useMemo, useState } from "react";
import { Search, Globe, GraduationCap, Check, RefreshCw, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import {
  listTemplates,
  listSubscriptions,
  subscribeTemplate,
  refreshSubscription,
  unsubscribeTemplate,
  flagEmoji,
  getLastCountry,
  setLastCountry,
} from "@/lib/templatesApi";

// ─────────────────────────────────────────────────────────────────────
// TemplatesPickerSheet — three-screen flow:
//   1. List subscriptions (with refresh/remove) + "Add from template" CTA
//   2. Country picker (search + alphabetic). Shows ✓ next to already-subbed.
//   3. Country detail — Holidays card + School Terms card (with region picker
//      when multiple regions exist). Subscribe button per card.
//
// Auto-detects user country from navigator.language or the last-picked
// pref. Subscribing creates a sub-calendar and materialises events.
// ─────────────────────────────────────────────────────────────────────

const SCREEN_LIST = "list";
const SCREEN_COUNTRY_PICKER = "country";
const SCREEN_COUNTRY_DETAIL = "detail";

export function TemplatesPickerSheet({ open, onClose, onChanged }) {
  const { family } = useAuth();
  const [screen, setScreen] = useState(SCREEN_LIST);
  const [subscriptions, setSubscriptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCC, setSelectedCC] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !family?.id) return;
    setScreen(SCREEN_LIST);
    setLoading(true);
    Promise.all([listTemplates(), listSubscriptions(family.id)])
      .then(([tpls, subs]) => { setTemplates(tpls); setSubscriptions(subs); })
      .finally(() => setLoading(false));
  }, [open, family?.id]);

  const refresh = async () => {
    if (!family?.id) return;
    setLoading(true);
    try {
      const subs = await listSubscriptions(family.id);
      setSubscriptions(subs);
      onChanged?.();
    } finally { setLoading(false); }
  };

  const handleSubscribe = async (template, region) => {
    try {
      await subscribeTemplate(family.id, template.id, { region });
      toast.success(`${template.country_name}${region ? ' · ' + region : ''} added`);
      setLastCountry(template.country_code);
      await refresh();
      setScreen(SCREEN_LIST);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't subscribe");
    }
  };

  const handleRefresh = async (sub) => {
    try {
      const res = await refreshSubscription(family.id, sub.id);
      toast.success(`Refreshed — ${res.materialised} events`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Refresh failed");
    }
  };

  const handleUnsubscribe = async (sub) => {
    try {
      await unsubscribeTemplate(family.id, sub.id);
      toast.success("Removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Couldn't remove");
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={
        screen === SCREEN_LIST ? "Calendar templates" :
        screen === SCREEN_COUNTRY_PICKER ? "Pick a country" :
        templates.find((t) => t.country_code === selectedCC)?.country_name || "Country"
      }
      fullHeight
    >
      {loading && (
        <div className="p-10 text-center text-sm text-muted-foreground" data-testid="tpl-loading">Loading…</div>
      )}

      {!loading && screen === SCREEN_LIST && (
        <SubsListPane
          subscriptions={subscriptions}
          onAdd={() => { setScreen(SCREEN_COUNTRY_PICKER); }}
          onRefresh={handleRefresh}
          onUnsubscribe={handleUnsubscribe}
        />
      )}

      {!loading && screen === SCREEN_COUNTRY_PICKER && (
        <CountryPickerPane
          templates={templates}
          subscriptions={subscriptions}
          query={query}
          setQuery={setQuery}
          onPick={(cc) => { setSelectedCC(cc); setScreen(SCREEN_COUNTRY_DETAIL); }}
        />
      )}

      {!loading && screen === SCREEN_COUNTRY_DETAIL && selectedCC && (
        <CountryDetailPane
          countryCode={selectedCC}
          templates={templates}
          subscriptions={subscriptions}
          onBack={() => setScreen(SCREEN_COUNTRY_PICKER)}
          onSubscribe={handleSubscribe}
        />
      )}
    </BottomSheet>
  );
}


function SubsListPane({ subscriptions, onAdd, onRefresh, onUnsubscribe }) {
  const live = subscriptions.filter((s) => !s.deleted_at);
  return (
    <div className="px-4 pb-6 space-y-3" data-testid="tpl-subs-list">
      <p className="text-sm text-muted-foreground">
        Holidays and school-term dates, auto-refreshed on app start every 14 days.
      </p>
      <button
        type="button"
        onClick={onAdd}
        data-testid="tpl-add-btn"
        className="w-full h-12 rounded-xl bg-foreground text-background font-medium tap inline-flex items-center justify-center gap-2"
      >
        <Globe className="w-4 h-4" /> Add from template
      </button>

      {live.length === 0 ? (
        <div className="rounded-xl bg-secondary/40 p-5 text-center text-sm text-muted-foreground">
          No template calendars yet. Add public holidays for your country in seconds.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="tpl-subs-items">
          {live.map((s) => (
            <li
              key={s.id}
              data-testid={`tpl-sub-${s.id}`}
              className="rounded-xl border border-border/60 bg-background p-3 flex items-center gap-3"
            >
              <span className="text-2xl shrink-0">{flagEmoji(s.country_code)}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {s.country_name}
                  {s.region_label ? ` — ${s.region_label}` : ""}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {s.kind === "holidays" ? "Public holidays" : "School terms"}
                  {" · "}
                  {s.materialised_count || 0} events
                  {s.last_refreshed_at ? ` · refreshed ${humanAgo(s.last_refreshed_at)}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRefresh(s)}
                data-testid={`tpl-refresh-${s.id}`}
                aria-label="Refresh"
                className="w-9 h-9 rounded-full bg-secondary text-foreground tap inline-flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onUnsubscribe(s)}
                data-testid={`tpl-remove-${s.id}`}
                aria-label="Remove"
                className="w-9 h-9 rounded-full bg-destructive/10 text-destructive tap inline-flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function CountryPickerPane({ templates, subscriptions, query, setQuery, onPick }) {
  const subbedCCs = useMemo(() => {
    return new Set(subscriptions.filter((s) => !s.deleted_at).map((s) => s.country_code));
  }, [subscriptions]);

  // Group by country (each country may have both holidays + school_terms).
  const byCountry = useMemo(() => {
    const map = new Map();
    for (const t of templates) {
      const cur = map.get(t.country_code) || { code: t.country_code, name: t.country_name, kinds: [] };
      cur.kinds.push(t.kind);
      map.set(t.country_code, cur);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [templates]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return byCountry;
    return byCountry.filter((c) =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [byCountry, query]);

  // Float suggested (last picked + navigator detected) to the top
  const suggested = useMemo(() => {
    const last = getLastCountry();
    const guess = (navigator?.language || "").split("-")[1]?.toUpperCase();
    const set = new Set([last, guess].filter(Boolean));
    return byCountry.filter((c) => set.has(c.code));
  }, [byCountry]);

  return (
    <div className="px-4 pb-6 space-y-3" data-testid="tpl-country-picker">
      <label className="relative block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="tpl-country-search"
          placeholder="Search country…"
          className="w-full h-11 pl-10 pr-3 rounded-xl bg-secondary outline-none text-sm"
        />
      </label>

      {suggested.length > 0 && query === "" && (
        <div data-testid="tpl-suggested">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Suggested</p>
          {suggested.map((c) => (
            <CountryRow key={`s-${c.code}`} country={c} subbed={subbedCCs.has(c.code)} onPick={onPick} />
          ))}
        </div>
      )}

      <div data-testid="tpl-country-list">
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
          All countries ({filtered.length})
        </p>
        <ul className="rounded-xl border border-border/60 bg-background overflow-hidden divide-y divide-border/40">
          {filtered.map((c) => (
            <li key={c.code}>
              <CountryRow country={c} subbed={subbedCCs.has(c.code)} onPick={onPick} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


function CountryRow({ country, subbed, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(country.code)}
      data-testid={`tpl-country-${country.code}`}
      className="w-full flex items-center gap-3 px-3 py-2.5 tap hover:bg-secondary/40 transition-colors text-left"
    >
      <span className="text-2xl shrink-0">{flagEmoji(country.code)}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{country.name}</span>
        <span className="block text-[10.5px] text-muted-foreground">
          {country.kinds.includes("holidays") ? "Holidays" : ""}
          {country.kinds.includes("holidays") && country.kinds.includes("school_terms") ? " · " : ""}
          {country.kinds.includes("school_terms") ? "School terms" : ""}
        </span>
      </span>
      {subbed && <Check className="w-4 h-4 text-foreground/60" />}
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}


function CountryDetailPane({ countryCode, templates, subscriptions, onSubscribe }) {
  const countryTpls = templates.filter((t) => t.country_code === countryCode);
  const holidays = countryTpls.find((t) => t.kind === "holidays");
  const schoolTerms = countryTpls.find((t) => t.kind === "school_terms");
  const subbedHolidays = subscriptions.find((s) => !s.deleted_at && s.template_id === holidays?.id);
  const [selectedRegion, setSelectedRegion] = useState(
    schoolTerms?.regions?.length === 1 ? schoolTerms.regions[0] : null
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = async (template, region) => {
    setSubmitting(true);
    try { await onSubscribe(template, region); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="px-4 pb-6 space-y-4" data-testid={`tpl-detail-${countryCode}`}>
      {holidays && (
        <div className="rounded-xl border border-border/60 bg-background p-4 space-y-3" data-testid="tpl-detail-holidays">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-foreground/70 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-medium">Public holidays</h3>
              <p className="text-[11.5px] text-muted-foreground">
                {holidays.event_count} dates across {holidays.years.join(" + ")}.
                {holidays.source ? ` Source: ${holidays.source}.` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={submitting || !!subbedHolidays}
            onClick={() => submit(holidays, null)}
            data-testid="tpl-detail-subscribe-holidays"
            className={`w-full h-10 rounded-lg font-medium tap text-sm ${
              subbedHolidays
                ? "bg-secondary/40 text-muted-foreground cursor-not-allowed"
                : "bg-foreground text-background"
            }`}
          >
            {subbedHolidays ? "✓ Already added" : (submitting ? "Adding…" : "Add public holidays")}
          </button>
        </div>
      )}

      {schoolTerms ? (
        <div className="rounded-xl border border-border/60 bg-background p-4 space-y-3" data-testid="tpl-detail-school-terms">
          <div className="flex items-start gap-3">
            <GraduationCap className="w-5 h-5 text-foreground/70 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-medium">School terms</h3>
              <p className="text-[11.5px] text-muted-foreground">
                {schoolTerms.event_count} dates across {schoolTerms.years.join(" + ")}.
                {schoolTerms.regions?.length > 1 ? ` Pick a region below.` : ""}
              </p>
            </div>
          </div>

          {schoolTerms.regions?.length > 1 && (
            <div className="flex gap-2 flex-wrap" data-testid="tpl-region-picker">
              {schoolTerms.regions.map((r) => {
                const on = selectedRegion === r;
                const subbed = !!subscriptions.find(
                  (s) => !s.deleted_at && s.template_id === schoolTerms.id && s.region === r
                );
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setSelectedRegion(r)}
                    data-testid={`tpl-region-${r}`}
                    className={`h-9 px-3 rounded-full text-xs font-medium tap border ${
                      on
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background text-foreground border-border"
                    }`}
                  >
                    {r}
                    {subbed && <Check className="w-3 h-3 inline ml-1" />}
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            disabled={submitting || (schoolTerms.regions?.length > 1 && !selectedRegion)}
            onClick={() => submit(schoolTerms, selectedRegion)}
            data-testid="tpl-detail-subscribe-school-terms"
            className="w-full h-10 rounded-lg bg-foreground text-background font-medium tap text-sm disabled:opacity-50"
          >
            {submitting ? "Adding…" : `Add school terms${selectedRegion ? ' — ' + selectedRegion : ''}`}
          </button>
        </div>
      ) : (
        <div className="rounded-xl bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="tpl-school-terms-unavailable">
          School terms aren&apos;t bundled for this country yet. We&apos;re adding more &mdash; request priority in Settings.
        </div>
      )}
    </div>
  );
}


function humanAgo(iso) {
  try {
    const t = Date.parse(iso);
    const mins = Math.max(0, Math.floor((Date.now() - t) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return "—"; }
}
